/* eslint-disable react-refresh/only-export-components */
import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/lib/cn'
import { ScrollArea } from './ScrollArea'

/**
 * One surface, three weights.
 *
 * `plain` is what the global `.panel` CSS class does — 16px on surface-elevated with no border.
 * It is a variant here rather than a second thing to reach for, because a borderless card and a
 * bordered one differ by exactly one property and nothing else: same radius, same ground, same
 * padding contract. `EditableCard` used to hand-roll this same surface, which is how the design
 * system ended up with three ways to draw one box.
 *
 * `elevated` uses `shadow-ambient`, not the deprecated numeric aliases. Depth in this system is
 * tonal; the shadow is for things that genuinely float.
 *
 * Sizing (ADR-024): the container decides the height and the card fills it — `h-full min-h-0`.
 * In an auto-height parent `h-full` resolves to auto, so nothing changes there; a caller that needs
 * content height inside a sized parent opts out with `className="h-auto"`. The card becomes a flex
 * column ONLY when a direct child is a fill-mode body (`data-card-scroll="fill"`), never
 * unconditionally: three call sites use bare `flex` and rely on the default row direction, and
 * tailwind-merge sees no conflict between their `flex` and a base `flex-col`.
 *
 * `data-slot="card"` is the hook the app shell uses (`AppPageColumn`): a Card that is a direct child
 * of the page column is content-height, because that column is definite (index.css `height: 100%`)
 * and `h-full` would otherwise make a page-level Card page-tall. Opt in to fill with `flex-1`.
 */
const cardVariants = cva(
  'bg-surface-elevated rounded-lg h-full min-h-0 has-[>[data-card-scroll=fill]]:flex has-[>[data-card-scroll=fill]]:flex-col',
  {
    variants: {
      variant: {
        default: 'border border-border',
        plain: '',
        elevated: 'border border-border shadow-ambient',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
)

export interface CardProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof cardVariants> {
  /** Render element. Defaults to `div`. Use for `section`/`aside` where the outline matters. */
  as?: React.ElementType
  /** Only meaningful with `as="button"`: a card used as a toggle must not submit a surrounding form. */
  type?: 'button' | 'submit' | 'reset'
  /** Only meaningful with `as="button"` (a selectable provider tile that can be unavailable). */
  disabled?: boolean
}

const Card = React.forwardRef<HTMLDivElement, CardProps>(
  ({ className, variant, as, ...props }, ref) => {
    // A card is a surface, not a semantic element. `<section aria-labelledby>` and `<aside>` are
    // real structure a div cannot express, and without `as` those call sites had to keep the
    // legacy `.panel` class just to stay semantic — the component forcing a worse document
    // outline is the component's problem, not theirs. Mirrors PageContainer's `as`.
    const Component = (as ?? 'div') as React.ElementType
    return (
      <Component
        data-slot="card"
        className={cn(cardVariants({ variant, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Card.displayName = 'Card'

const CardHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('flex flex-col space-y-1.5 p-6', className)}
    {...props}
  />
))
CardHeader.displayName = 'CardHeader'

const CardTitle = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('font-semibold leading-none tracking-tight', className)}
    {...props}
  />
))
CardTitle.displayName = 'CardTitle'

const CardDescription = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('text-sm text-text-muted', className)}
    {...props}
  />
))
CardDescription.displayName = 'CardDescription'

/**
 * `scroll="none"` (default) is a plain padded body — exactly the DOM it always rendered.
 *
 * `scroll="fill"` (ADR-024) joins the card's flex chain: the body takes the space the header and
 * footer leave (`flex-1 min-h-0`), grows with its content while there is room, and scrolls inside
 * `ScrollArea` once the container cuts it short. `scrollLabel` names the viewport for assistive
 * tech while it overflows, so it is required. `maxItems` / `minItems` are the ScrollArea
 * item-count cap and floor (PRD M3/M4).
 */
export type CardContentProps = React.HTMLAttributes<HTMLDivElement> &
  (
    | { scroll?: 'none'; scrollLabel?: never; maxItems?: never; minItems?: never }
    | { scroll: 'fill'; scrollLabel: string; maxItems?: number; minItems?: number }
  )

const CardContent = React.forwardRef<HTMLDivElement, CardContentProps>(
  ({ className, scroll, scrollLabel, maxItems, minItems, children, ...props }, ref) => {
    if (scroll !== 'fill') {
      return (
        <div ref={ref} className={cn('p-6 pt-0', className)} {...props}>
          {children}
        </div>
      )
    }
    return (
      <div
        ref={ref}
        data-card-scroll="fill"
        className={cn('flex min-h-0 flex-1 flex-col p-6 pt-0', className)}
        {...props}
      >
        {/* Radix wraps the children in a `display: table` box (inline style) that grows to the content's
            min-content width, so one long truncating title or a wide table widens the body and the
            vertical-only viewport clips it. A block wrapper is as wide as the viewport, so the content's
            own `truncate` / `overflow-x-auto` works again. Callers must not hand-write containment for
            this (T07a did twice). */}
        <ScrollArea
          fill
          label={scrollLabel}
          maxItems={maxItems}
          minItems={minItems}
          viewportClassName="[&>div]:block!"
        >
          {children}
        </ScrollArea>
      </div>
    )
  }
)
CardContent.displayName = 'CardContent'

const CardFooter = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('flex items-center p-6 pt-0', className)}
    {...props}
  />
))
CardFooter.displayName = 'CardFooter'

export {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
  cardVariants,
}
